import React, { useEffect, useRef, useContext } from 'react'
import { useFilterStore } from '../../stores/useFilterStore'
import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'
import usePageInfo from '../../hooks/usePageInfo'
import { useNodePinning } from '../../hooks/useNodePinning'
import useSentimentUpdate from './useSentimentUpdate'
import { handleCombinedFilters } from '../MergedFilterPanel/recalculateFilterEffects'
import { handleCombinedHighlightFilters } from '../MergedFilterPanel/highlightFilterEffects'
import { useGraphStore } from '../../stores/useGraphStore'
import { formatTopicsFromClusters } from '../../utils/graphTopics'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

function haveFiltersChanged(current, previous) {
    return JSON.stringify(current) !== JSON.stringify(previous)
}

// Event emitter utility functions
const emitEvent = (eventName, data) => {
    if (window.myEventEmitter && myEventEmitter._events[eventName]) {
        myEventEmitter.emit(eventName, data)
    }
}

export default function FilterEventBridge() {
    const recalculate = useFilterStore(state => state.recalculate)
    const highlight = useFilterStore(state => state.highlight)
    const pinnedNodes = useFilterStore(state => state.pinnedNodes)
    const filterMode = useFilterStore(state => state.filterMode)
    const filteredStatements = useFilterStore(state => state.filteredStatements)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)

    const { flags = {}, username } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)

    // Refs to hold previous values
    const prevRecalculateRef = useRef(recalculate)
    const prevHighlightRef = useRef(highlight)
    const prevPinnedNodes = useRef(pinnedNodes)
    const prevFilteredStatements = useRef(filteredStatements)
    const prevSynthesisContext = useRef(null)

    const { label } = usePageInfo()
    useNodePinning()
    useSentimentUpdate(label, username, isPublic)

    useEffect(() => {
        // Skip old sigma-based filter effects when using new 3D graph
        if (flags.new_3d_graph) {
            console.log(
                '[InfraNodus] Skipping old filter effects (new 3D graph active)'
            )
            return
        }

        const highlightChanged = haveFiltersChanged(
            highlight,
            prevHighlightRef.current
        )
        const recalculateChanged = haveFiltersChanged(
            recalculate,
            prevRecalculateRef.current
        )
        const pinnedNodesChanged = haveFiltersChanged(
            pinnedNodes,
            prevPinnedNodes.current
        )

        if (recalculateChanged || highlightChanged || pinnedNodesChanged) {
            recalculateChanged &&
                handleCombinedFilters(
                    recalculate,
                    prevRecalculateRef.current,
                    filteredStatements
                )
            highlightChanged &&
                handleCombinedHighlightFilters(
                    highlight,
                    prevHighlightRef.current,
                    filteredStatements
                )
        }

        prevRecalculateRef.current = recalculate
        prevHighlightRef.current = highlight
        prevPinnedNodes.current = pinnedNodes
    }, [
        recalculate,
        highlight,
        filterMode,
        pinnedNodes,
        filteredStatements,
        flags.new_3d_graph,
    ])

    useEffect(() => {
        window.filterStore = useFilterStore
    }, [])

    useEffect(() => {
        const onTopicsReady = async () => {
            // Use compatibility layer - works with both new graph store and old graphFactory
            if (typeof analyticsPanel !== 'undefined') {
                await analyticsPanel.updateCategories(null, 'doNotShowOnGraph')
            }

            const rawClusters = useGraphStore.getState().getCommunities()
            const communities = formatTopicsFromClusters(rawClusters)
            console.log('Topics loaded:', communities)

            if (communities.length > 0) {
                useZustand.getState().setTopics(communities, true)
                useFilterStore
                    .getState()
                    .updateFilteredStatementsWithCommunities()
            } else {
                console.warn('No communities loaded from graph')
            }
        }
        if (window.myEventEmitter) {
            myEventEmitter.on('Modularity', onTopicsReady)
        }
        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener('Modularity', onTopicsReady)
            }
        }
    }, [])

    useEffect(() => {
        if (
            label !== 'Top Concepts Synthesis' &&
            label !== 'Top Graphs Synthesis'
        )
            return
        const onSynthesisReady = async () => {
            console.log('Synthesis ready')

            const graphData = useGraphStore.getState().graphData
            const rawStatements =
                graphData?.entriesAndGraphOfContext?.statements || []
            if (rawStatements.length > 0) {
                const statements = rawStatements.map(s => s.content)
                const uids = rawStatements.map(s => String(s.id))
                const statementHashtags = Object.fromEntries(
                    rawStatements.map(s => [
                        String(s.id),
                        s.statementHashtags || [],
                    ])
                )
                addPrivateStatementsToStore(
                    statements,
                    label,
                    uids,
                    statementHashtags
                )
            } else {
                console.warn('No statements loaded in graph for synthesis')
            }
        }
        if (window.myEventEmitter) {
            myEventEmitter.on('Synthesis', onSynthesisReady)
        }
        return () =>
            window.myEventEmitter &&
            myEventEmitter.removeListener('Synthesis', onSynthesisReady)
    }, [label, addPrivateStatementsToStore])

    const handleCommunitySelection = async selectedCommunities => {
        const loadedTopics = useFilterStore.getState().availableOptions.topics

        const newTopics = loadedTopics.filter(topic =>
            selectedCommunities.includes(topic.id.toString())
        )

        useFilterStore.getState().setHighlight({ topics: newTopics })
    }

    useEffect(() => {
        if (window.myEventEmitter) {
            window.myEventEmitter.on(
                'selectCommunityFilter',
                handleCommunitySelection
            )

            return () => {
                if (
                    window.myEventEmitter &&
                    window.myEventEmitter._events['selectCommunityFilter']
                ) {
                    window.myEventEmitter.removeListener(
                        'selectCommunityFilter',
                        handleCommunitySelection
                    )
                }
            }
        }
    }, [handleCommunitySelection])

    const handleSentimentClick = sentiment => {
        const label = useZustand.getState().currentContext
        if (!label) {
            console.error('No label available')
            return
        }
        const availableSentimentsForFilter = useFilterStore.getState()
            .availableOptions.sentiments

        const loadedSentiments = useFilterStore.getState().recalculate
            .sentiments

        // if sentiment is already selected, remove it
        if (loadedSentiments.includes(sentiment)) {
            window.highlightedSentiment = []

            useFilterStore.getState().setRecalculate({
                sentiments: [],
            })
            return
        }

        // if sentiment is not available, do nothing
        if (!availableSentimentsForFilter.includes(sentiment)) {
            emitEvent('sentimentFilterUpdate', [])
            return
        }

        const newSentiments = [sentiment]

        window.highlightedSentiment = [...newSentiments]

        useFilterStore.getState().setRecalculate({
            sentiments: newSentiments,
        })
    }

    useEffect(() => {
        if (window.myEventEmitter) {
            const onSentimentClick = sentiment => {
                handleSentimentClick(sentiment)
            }

            window.myEventEmitter.on('sentimentClick', onSentimentClick)

            return () => {
                window.myEventEmitter.removeListener(
                    'sentimentClick',
                    onSentimentClick
                )
            }
        }
    }, [handleSentimentClick])

    return null
}
